/**
 * Program events — V103-3-6 / V103-6-3 (migration 349a, `program_events`).
 *
 * Facts that come from OUTSIDE GAC Pro and that a person records, so the
 * program pages and Today read them instead of guessing:
 *
 *   govramp_status_granted  GovRAMP granted a tier { tier, granted_on }. The
 *                           tier the workspace HOLDS; the profile's tier is the
 *                           one it is assessed against (it may be pursuing the
 *                           next one). A tier is never claimed without this.
 *   govramp_lapse_notice    GovRAMP notified a lapse { notice_date, note? }.
 *                           GAC Pro never computes a lapse date (design rule).
 *   ready_booking           A GovRAMP Ready assessment is booked { firm,
 *                           assessment_date }. ONE record on the GovRAMP
 *                           profile; FedRAMP Class A reads the same row
 *                           (design acceptance #4: nobody records it twice).
 *   conmon_item_done        A ConMon checklist item is done { cycle_id, item }.
 *   higher_class_booking    A FedRAMP Class B/C/D assessment is booked { firm,
 *                           assessment_date } (Class A profiles).
 *
 * One live row per profile × kind (× cycle item). Recording a kind that has a
 * live row replaces it: the old row ends with `superseded_by` (a booking
 * change keeps its trail). Rows are append-only; they are ended, never
 * edited or deleted.
 *
 * Every read and write is scoped by the caller's workspace; another
 * workspace's profile, cycle or event is "not found".
 */
import { z } from 'zod';
import { generateRandomId } from '../utils/audit-crypto.js';
import { sqlIn } from '../utils/sql-in.js';
import { GOVRAMP_TIERS } from '../programs/manifests.js';
export const PROGRAM_EVENT_KINDS = [
    'govramp_status_granted', 'govramp_lapse_notice', 'ready_booking', 'conmon_item_done', 'higher_class_booking',
];
export class ProgramEventError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ProgramEventError';
    }
}
const fail = (code, message) => { throw new ProgramEventError(code, message); };
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const day = (field) => z.string().regex(DAY, `${field} must be a date (YYYY-MM-DD).`)
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v, `${field} must be a real date.`);
const firm = z.string().trim().min(1, 'Name the assessor firm.').max(200);
/** Per-kind data, and which program the kind belongs to. */
const KIND_SPEC = {
    govramp_status_granted: { program: 'govramp', data: z.object({ tier: z.enum(GOVRAMP_TIERS), granted_on: day('granted_on') }).strict(), past: 'granted_on' },
    govramp_lapse_notice: { program: 'govramp', data: z.object({ notice_date: day('notice_date'), note: z.string().trim().max(500).optional() }).strict(), past: 'notice_date' },
    ready_booking: { program: 'govramp', data: z.object({ firm, assessment_date: day('assessment_date') }).strict(), past: null },
    conmon_item_done: { program: 'govramp', data: z.object({ cycle_id: z.string().min(1).max(100), item: z.string().regex(/^[a-z0-9_]{1,80}$/, 'item is a checklist item key.') }).strict(), past: null },
    higher_class_booking: { program: 'fedramp-cr26', data: z.object({ firm, assessment_date: day('assessment_date') }).strict(), past: null },
};
const toEvent = (r) => {
    let data = {};
    try {
        data = JSON.parse(r.data_json);
    }
    catch {
        data = {};
    }
    const { data_json: _omit, ...rest } = r;
    return { ...rest, data };
};
/** The program a kind belongs to (a GovRAMP fact can't be filed on SOC 2). */
export function programOfEventKind(kind) {
    return KIND_SPEC[kind].program;
}
export function isProgramEventKind(v) {
    return typeof v === 'string' && PROGRAM_EVENT_KINDS.includes(v);
}
async function activeProfile(db, workspaceId, profileId) {
    const p = (await db.query(`SELECT profile_id, program, certification_class, status FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`, [profileId, workspaceId])).results[0];
    if (!p)
        fail('not_found', 'Not found');
    if (p.status !== 'active')
        fail('conflict', 'This program is archived.');
    return p;
}
/** Record a fact; a live row of the same kind (and cycle item) ends, superseded by the new one. */
export async function recordProgramEvent(db, input) {
    if (!isProgramEventKind(input.kind))
        fail('invalid', `kind must be one of ${PROGRAM_EVENT_KINDS.join(', ')}.`);
    const kind = input.kind;
    const spec = KIND_SPEC[kind];
    const actor = typeof input.actorUserId === 'string' ? input.actorUserId.trim() : '';
    if (!actor || actor.startsWith('system:'))
        fail('invalid', 'A person records program events.');
    const profile = await activeProfile(db, input.workspaceId, input.profileId);
    if (profile.program !== spec.program)
        fail('invalid', `${kind} belongs to the ${spec.program} program.`);
    if (kind === 'higher_class_booking' && profile.certification_class !== 'A')
        fail('invalid', 'A Class B, C or D booking is recorded on a Class A profile.');
    const parsed = spec.data.safeParse(input.data ?? {});
    if (!parsed.success)
        fail('invalid', parsed.error.issues[0]?.message ?? 'Invalid data.');
    const data = parsed.data;
    const now = input.now ?? new Date();
    const today = now.toISOString().slice(0, 10);
    if (spec.past && typeof data[spec.past] === 'string' && data[spec.past] > today)
        fail('invalid', `${spec.past} cannot be in the future.`);
    if (kind === 'govramp_lapse_notice' && typeof data.note === 'string' && !data.note)
        delete data.note;
    let subject = null;
    if (kind === 'conmon_item_done') {
        const cycle = (await db.query(`SELECT cycle_id, status, ended_at FROM conmon_cycles WHERE cycle_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [data.cycle_id, input.workspaceId, input.profileId])).results[0];
        if (!cycle)
            fail('not_found', 'ConMon cycle not found.');
        if (cycle.ended_at)
            fail('conflict', 'This cycle was rescheduled; use its replacement.');
        if (cycle.status === 'submitted')
            fail('conflict', 'This cycle is already submitted.');
        subject = `${data.cycle_id}:${data.item}`;
    }
    const live = (await db.query(`SELECT event_id FROM program_events WHERE workspace_id = ? AND profile_id = ? AND kind = ? AND COALESCE(subject_key, '') = ? AND ended_at IS NULL LIMIT 1`, [input.workspaceId, input.profileId, kind, subject ?? ''])).results[0];
    if (live && kind === 'conmon_item_done')
        fail('conflict', 'This item is already marked done.');
    const id = input.eventId ?? generateRandomId('pev');
    const at = now.toISOString();
    const statements = [];
    // The old row ends first (it frees the live-row unique slot), pointing at the new one.
    if (live) {
        statements.push({
            sql: `UPDATE program_events SET ended_at = ?, ended_by = ?, end_reason = 'replaced', superseded_by = ?
             WHERE event_id = ? AND workspace_id = ? AND ended_at IS NULL`,
            params: [at, actor, id, live.event_id, input.workspaceId],
        });
    }
    statements.push({
        sql: `INSERT INTO program_events (event_id, workspace_id, profile_id, kind, subject_key, data_json, recorded_by, recorded_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [id, input.workspaceId, input.profileId, kind, subject, JSON.stringify(data), actor, at],
    });
    await db.batch(statements);
    return (await getProgramEvent(db, input.workspaceId, id));
}
export async function getProgramEvent(db, workspaceId, eventId) {
    const r = await db.query(`SELECT * FROM program_events WHERE event_id = ? AND workspace_id = ? LIMIT 1`, [eventId, workspaceId]);
    return r.results[0] ? toEvent(r.results[0]) : null;
}
/** End a live event (withdrawn, recorded in error). The row stays. */
export async function endProgramEvent(db, input) {
    const actor = typeof input.actorUserId === 'string' ? input.actorUserId.trim() : '';
    if (!actor || actor.startsWith('system:'))
        fail('invalid', 'A person ends program events.');
    const e = await getProgramEvent(db, input.workspaceId, input.eventId);
    if (!e || e.profile_id !== input.profileId)
        fail('not_found', 'Not found');
    if (e.ended_at)
        fail('conflict', 'This record has already ended.');
    const reason = typeof input.reason === 'string' && input.reason.trim() ? input.reason.trim().slice(0, 500) : 'removed';
    await db.execute(`UPDATE program_events SET ended_at = ?, ended_by = ?, end_reason = ? WHERE event_id = ? AND workspace_id = ? AND ended_at IS NULL`, [(input.now ?? new Date()).toISOString(), actor, reason, input.eventId, input.workspaceId]);
    return (await getProgramEvent(db, input.workspaceId, input.eventId));
}
/** A profile's events, newest first (live only unless `includeEnded`). */
export async function listProgramEvents(db, workspaceId, profileId, opts = {}) {
    const kinds = [...new Set((opts.kinds ?? []).filter(isProgramEventKind))];
    const inKinds = sqlIn(kinds);
    const r = await db.query(`SELECT * FROM program_events WHERE workspace_id = ? AND profile_id = ?
       ${opts.includeEnded ? '' : 'AND ended_at IS NULL'}
       ${kinds.length ? `AND kind IN ${inKinds.sql}` : ''}
      ORDER BY recorded_at DESC, event_id DESC`, kinds.length ? [workspaceId, profileId, ...inKinds.params] : [workspaceId, profileId]);
    return r.results.map(toEvent);
}
/** The live event of a kind on a profile, or null. */
export async function liveProgramEvent(db, workspaceId, profileId, kind) {
    return (await listProgramEvents(db, workspaceId, profileId, { kinds: [kind] }))[0] ?? null;
}
//# sourceMappingURL=program-event-service.js.map